import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChevronRight,
  Building2,
  Layers,
  Globe2,
} from 'lucide-react';

/* ------------------------------------------------------------------ *
 *  EnergiPro Group — shared company-page building blocks
 *  These wrap the SAME Tailwind utilities already used across the site
 *  (Oswald headings, slate/orange palette, py-20 md:py-28 sections,
 *  reveal-stagger, rounded-none uppercase CTAs). No new design tokens.
 * ------------------------------------------------------------------ */

/* ---------- SEO (SPA-safe, no extra dependency) ---------- */
export function usePageSeo(title: string, description: string) {
  useEffect(() => {
    document.title = title;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', description);
  }, [title, description]);
}


/* ---------- Scroll reveal (matches the site's existing section behaviour) ---------- */
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.05, rootMargin: '0px 0px 0px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

/* ---------- Section wrapper (matches existing rhythm) ---------- */
type Tone = 'white' | 'muted' | 'gradient' | 'dark';

const toneClass: Record<Tone, string> = {
  white: 'bg-white',
  muted: 'bg-slate-50/80 section-pattern-subtle',
  gradient: 'bg-gradient-to-b from-slate-50 to-white',
  dark: 'bg-slate-900 text-white',
};

export function Section({
  id,
  tone = 'white',
  children,
}: {
  id?: string;
  tone?: Tone;
  children: React.ReactNode;
}) {
  const { ref, visible } = useReveal<HTMLElement>();
  return (
    <section
      ref={ref}
      id={id}
      className={`py-20 md:py-28 ${toneClass[tone]} reveal-stagger ${visible ? 'is-visible' : ''}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">{children}</div>
    </section>
  );
}

/* ---------- Section heading (orange rule + Oswald caps) ---------- */
export function SectionHeading({
  eyebrow,
  title,
  description,
  dark = false,
  center = false,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  dark?: boolean;
  center?: boolean;
}) {
  return (
    <div className={`${center ? 'text-center mx-auto' : ''} max-w-3xl mb-12`}>
      {eyebrow && (
        <p className="text-orange-600 text-xs font-bold uppercase tracking-[0.2em] mb-3">
          {eyebrow}
        </p>
      )}
      <h2
        className={`font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight ${
          dark ? 'text-white' : 'text-slate-900'
        }`}
      >
        {title}
      </h2>
      <div className={`h-1 w-16 bg-orange-600 mt-4 ${center ? 'mx-auto' : ''}`} />
      {description && (
        <p className={`mt-5 text-base leading-relaxed ${dark ? 'text-slate-300' : 'text-slate-500'}`}>
          {description}
        </p>
      )}
    </div>
  );
}

/* ---------- Buttons (identical utilities to existing site CTAs) ---------- */
export function PrimaryCTA({
  to,
  href,
  children,
}: {
  to?: string;
  href?: string;
  children: React.ReactNode;
}) {
  const cls =
    'inline-flex items-center justify-center gap-2 rounded-none px-10 py-4 text-base font-extrabold uppercase tracking-wider bg-orange-600 hover:bg-orange-700 text-white transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.97] shadow-[0_0_30px_rgba(234,88,12,0.3)] hover:shadow-[0_0_40px_rgba(234,88,12,0.5)]';
  if (href) {
    return (
      <a href={href} className={cls}>
        {children} <ArrowRight className="w-4 h-4" />
      </a>
    );
  }
  return (
    <Link to={to || '/contact'} className={cls}>
      {children} <ArrowRight className="w-4 h-4" />
    </Link>
  );
}

export function SecondaryCTA({
  to,
  href,
  children,
  onDark = true,
}: {
  to?: string;
  href?: string;
  children: React.ReactNode;
  onDark?: boolean;
}) {
  const cls = `inline-flex items-center justify-center gap-2 rounded-none px-10 py-4 text-base font-bold uppercase tracking-wider border-2 transition-all duration-300 ease-out hover:-translate-y-0.5 active:scale-[0.97] ${
    onDark
      ? 'border-white/70 text-white hover:bg-white hover:text-slate-900'
      : 'border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white'
  }`;
  if (href) {
    return (
      <a href={href} className={cls}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to || '/contact'} className={cls}>
      {children}
    </Link>
  );
}

/* ---------- Inner-page hero (same construction as /about) ---------- */
export function CompanyHero({
  eyebrow,
  icon,
  heroLogo,
  title,
  tagline,
  lead,
  sub,
  primary,
  secondary,
  badges,
}: {
  eyebrow: string[];
  icon: React.ReactNode;
  heroLogo?: string;
  title: string;
  tagline: string;
  lead: string;
  sub: string;
  primary: { label: string; to?: string; href?: string };
  secondary: { label: string; to?: string; href?: string };
  badges: string[];
}) {
  return (
    <section className="relative bg-slate-950 text-white pt-32 pb-20 md:pt-36 md:pb-24 border-b-2 border-orange-600 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.06] bg-[radial-gradient(circle_at_20%_20%,#ea580c_0,transparent_45%)]" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {heroLogo && (
          <div className="mb-6">
            <img
              src={heroLogo}
              alt="Company logo"
              className="h-12 md:h-14 lg:h-16 w-auto object-contain"
            />
          </div>
        )}
        <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-5">
          {eyebrow.join('  •  ')}
        </p>

        <div className="flex items-start gap-4">
          <span className="hidden md:flex w-12 h-12 shrink-0 items-center justify-center border border-orange-600/50 text-orange-500">
            {icon}
          </span>
          <div className="max-w-4xl">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold uppercase text-white leading-[1.05] animate-slide-up">
              {title}
            </h1>
            <p className="font-heading text-xl md:text-2xl font-bold uppercase tracking-wide text-orange-500 mt-3">
              {tagline}
            </p>
          </div>
        </div>

        <p className="mt-7 max-w-3xl text-slate-200 text-base md:text-lg leading-relaxed">{lead}</p>
        <p className="mt-4 max-w-3xl text-slate-400 text-sm md:text-base leading-relaxed">{sub}</p>

        <div className="mt-8 flex flex-wrap gap-3">
          {badges.map((b) => (
            <span
              key={b}
              className="inline-flex items-center gap-2 border border-white/15 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-200"
            >
              <span className="w-1.5 h-1.5 bg-orange-500" />
              {b}
            </span>
          ))}
        </div>

        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <PrimaryCTA to={primary.to} href={primary.href}>
            {primary.label}
          </PrimaryCTA>
          <SecondaryCTA to={secondary.to} href={secondary.href}>
            {secondary.label}
          </SecondaryCTA>
        </div>
      </div>
    </section>
  );
}

/* ---------- Card primitives (mirror the existing card language) ---------- */
export function FeatureCard({
  icon,
  label,
  title,
  body,
  tags,
}: {
  icon: React.ReactNode;
  label?: string;
  title: string;
  body: string;
  tags?: string[];
}) {
  return (
    <div className="group bg-white border border-slate-200 p-7 transition-all duration-300 hover:-translate-y-1 hover:border-orange-600/50 hover:shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
      <div className="text-orange-600 mb-5">{icon}</div>
      {label && (
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-2">{label}</p>
      )}
      <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900 mb-3">
        {title}
      </h3>
      <p className="text-sm text-slate-500 leading-relaxed">{body}</p>
      {tags && (
        <div className="mt-5 flex flex-wrap gap-2">
          {tags.map((t) => (
            <span
              key={t}
              className="text-[11px] font-bold uppercase tracking-wider text-orange-700 bg-orange-50 border border-orange-200 px-2.5 py-1"
            >
              {t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function StatCard({
  label,
  value,
  note,
  icon,
}: {
  label: string;
  value: string;
  note: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-slate-200 p-7">
      <div className="text-orange-600 mb-4">{icon}</div>
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-2">{label}</p>
      <p className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-2">{value}</p>
      <p className="text-sm text-slate-500 leading-relaxed">{note}</p>
    </div>
  );
}

export function ListPanel({
  title,
  items,
  icon,
}: {
  title: string;
  items: string[];
  icon: React.ReactNode;
}) {
  return (
    <div className="bg-white border border-slate-200 p-7 h-full">
      <div className="flex items-center gap-3 mb-5">
        <span className="text-orange-600">{icon}</span>
        <h3 className="font-heading text-lg font-bold uppercase tracking-wide text-slate-900">
          {title}
        </h3>
      </div>
      <ul className="space-y-2.5">
        {items.map((i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed">
            <ChevronRight className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
            <span>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ---------- Full-width quote/assurance strip (existing pattern) ---------- */
export function AssuranceStrip({ text }: { text: string }) {
  return (
    <div className="bg-slate-950 border-y-2 border-orange-600 py-8">
      <p className="max-w-5xl mx-auto px-4 text-center font-heading text-lg md:text-xl font-bold uppercase tracking-wide text-white">
        {text}
      </p>
    </div>
  );
}

/* ---------- CTA banner (same as site-wide closing banner) ---------- */
export function CTABanner({
  heading,
  sub,
  primary,
  secondary,
}: {
  heading: string;
  sub: string;
  primary: { label: string; to?: string; href?: string };
  secondary: { label: string; to?: string; href?: string };
}) {
  return (
    <section className="py-20 md:py-28 bg-slate-950 text-white border-t-2 border-orange-600">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
          {heading}
        </h2>
        <div className="h-1 w-16 bg-orange-600 mt-4 mx-auto" />
        <p className="mt-6 text-slate-300 text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
          {sub}
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
          <PrimaryCTA to={primary.to} href={primary.href}>
            {primary.label}
          </PrimaryCTA>
          <SecondaryCTA to={secondary.to} href={secondary.href}>
            {secondary.label}
          </SecondaryCTA>
        </div>
      </div>
    </section>
  );
}

/* ---------- "A Member of EnergiPro Group" ---------- */
type GroupKey = 'energipro' | 'kimko' | 'petronix';

const GROUP: Record<
  GroupKey,
  { name: string; country: string; scope: string; to: string }
> = {
  energipro: {
    name: 'EnergiPro Solutions',
    country: 'Saudi Arabia',
    scope: 'Well testing, pressure control, oilfield equipment & field services',
    to: '/',
  },
  kimko: {
    name: 'KIMKO',
    country: 'United Arab Emirates',
    scope: 'MEP engineering, HVAC design, building services & authority approvals',
    to: '/companies/kimko',
  },
  petronix: {
    name: 'Petronix Energy Services',
    country: 'Egypt',
    scope: 'Energy & industrial services, equipment rental, technical manpower',
    to: '/companies/petronix',
  },
};

export function GroupMemberSection({ current }: { current: GroupKey }) {
  const { ref, visible } = useReveal<HTMLElement>();
  return (
    <section
      ref={ref}
      className={`py-20 md:py-28 bg-slate-950 text-white reveal-stagger ${visible ? 'is-visible' : ''}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <p className="text-orange-500 text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Group Structure
          </p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight text-white">
            A Member of EnergiPro Group
          </h2>
          <div className="h-1 w-16 bg-orange-600 mt-4 mx-auto" />
          <p className="mt-6 text-slate-300 leading-relaxed">
            {GROUP[current].name} operates as the {GROUP[current].country} arm of EnergiPro Group — a
            regional platform delivering energy, industrial and engineering services across Saudi
            Arabia, the UAE and Egypt. Shared management systems, shared standards, one commercial
            interface.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {(Object.keys(GROUP) as GroupKey[]).map((k) => {
            const c = GROUP[k];
            const isCurrent = k === current;
            return (
              <Link
                key={k}
                to={c.to}
                className={`block border p-7 transition-all duration-300 hover:-translate-y-1 ${
                  isCurrent
                    ? 'border-orange-600 bg-orange-600/10'
                    : 'border-white/15 bg-white/5 hover:border-orange-600/60'
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-5 h-5 text-orange-500" />
                  <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    {c.country}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-bold uppercase tracking-wide text-white mb-2">
                  {c.name}
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">{c.scope}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-orange-500">
                  {isCurrent ? 'You are here' : 'Visit company'} <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            );
          })}
        </div>

        <p className="mt-10 text-center text-xs text-slate-500 max-w-3xl mx-auto leading-relaxed">
          Group companies are separately registered legal entities operating under common ownership
          and shared governance. Cross-border scopes can be contracted through a single group
          commercial interface where the client requires it.
        </p>

        <div className="mt-8 flex justify-center gap-3">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
            <Layers className="w-4 h-4 text-orange-600" /> One group
          </span>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400">
            <Globe2 className="w-4 h-4 text-orange-600" /> Three countries
          </span>
        </div>
      </div>
    </section>
  );
}
